import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { useCart } from '../contexts/CartContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { stripePromise } from '../config/stripe';
import { stripeService, CreatePaymentIntentRequest } from '../services/stripeService';
import CheckoutForm from '../components/CheckoutForm';

// Demo Payment Form Component for development mode
const DemoPaymentForm: React.FC<{
  shippingInfo: any;
  cartItems: any[];
  onPaymentSuccess: (orderConfirmation: any) => void;
  onPaymentError: (error: string) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  validateForm: () => boolean;
}> = ({ shippingInfo, cartItems, onPaymentSuccess, onPaymentError, isProcessing, setIsProcessing, validateForm }) => {
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate successful payment
      onPaymentSuccess({
        paymentIntentId: 'pi_demo_' + Date.now(),
        status: 'succeeded',
        items: cartItems,
        total: cartItems.reduce((sum, item) => sum + (item.card.price * item.quantity), 0),
        customerEmail: shippingInfo.email,
        shippingAddress: shippingInfo,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Demo payment failed';
      setError(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Demo Mode:</strong> This is a demonstration checkout flow. No real payment will be processed.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, p: 2, border: '1px dashed', borderColor: 'text.secondary', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Demo Payment Form
        </Typography>
        <Typography variant="body2" color="text.secondary">
          In production, this would be a secure Stripe payment form.
        </Typography>
      </Box>

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={isProcessing}
        sx={{ py: 1.5 }}
      >
        {isProcessing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            Processing Demo Payment...
          </Box>
        ) : (
          `Demo Pay $${cartItems.reduce((sum, item) => sum + (item.card.price * item.quantity), 0).toFixed(2)}`
        )}
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
        This is a demonstration checkout flow
      </Typography>
    </Box>
  );
};

const CheckoutPage: React.FC = () => {
  const { cartItems, getCartTotal } = useCart();
  const { mode } = useTheme();
  const navigate = useNavigate();
  const [shippingInfo, setShippingInfo] = useState({
    name: '',
    email: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [isInitializing, setIsInitializing] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const handleInputChange = (field: string, value: string) => {
    setShippingInfo(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    const requiredFields = ['name', 'email', 'line1', 'city', 'state', 'postalCode'];
    const missingFields = requiredFields.filter(field => !shippingInfo[field as keyof typeof shippingInfo]);
    
    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shippingInfo.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handlePaymentSuccess = (orderConfirmation: any) => {
    setIsProcessing(false);
    navigate('/checkout/success', { 
      state: { orderConfirmation } 
    });
  };

  const handlePaymentError = (error: string) => {
    setIsProcessing(false);
    setError(error);
  };

  const initializePayment = useCallback(async () => {
    if (cartItems.length === 0) return;
    
    try {
      setIsInitializing(true);
      setError(null);

      const request: CreatePaymentIntentRequest = {
        items: cartItems,
        customerEmail: shippingInfo.email || 'demo@example.com', // Use demo email if not provided yet
        shippingAddress: {
          name: shippingInfo.name || 'Demo User',
          line1: shippingInfo.line1 || '123 Demo St',
          line2: shippingInfo.line2,
          city: shippingInfo.city || 'Demo City',
          state: shippingInfo.state || 'DC',
          postalCode: shippingInfo.postalCode || '12345',
          country: shippingInfo.country,
        },
      };

      const response = await stripeService.createPaymentIntent(request);
      
      // Check if we got a mock client secret (development mode)
      if (response.clientSecret.includes('mock') || response.clientSecret.includes('_secret_')) {
        console.warn('Using mock client secret, switching to demo mode');
        setIsDemoMode(true);
        setError(null);
      } else {
        setClientSecret(response.clientSecret);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize payment';
      
      // If it's a development mode error, enable demo mode
      if (errorMessage.includes('API not available') || errorMessage.includes('mock data') || errorMessage.includes('Cannot connect')) {
        setIsDemoMode(true);
        setError(null); // Clear error for demo mode
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsInitializing(false);
    }
  }, [cartItems, shippingInfo]);

  // Initialize payment when component mounts
  useEffect(() => {
    initializePayment();
  }, [cartItems, initializePayment]);

  if (cartItems.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" gutterBottom>
            Your cart is empty
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Add some cards to your cart before checking out.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/cards')}
            sx={{ px: 4, py: 1.5 }}
          >
            Browse Cards
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Checkout
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Shipping Information */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Shipping Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name *"
                  value={shippingInfo.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={isProcessing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email *"
                  type="email"
                  value={shippingInfo.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={isProcessing}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1 *"
                  value={shippingInfo.line1}
                  onChange={(e) => handleInputChange('line1', e.target.value)}
                  disabled={isProcessing}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={shippingInfo.line2}
                  onChange={(e) => handleInputChange('line2', e.target.value)}
                  disabled={isProcessing}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City *"
                  value={shippingInfo.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  disabled={isProcessing}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth disabled={isProcessing}>
                  <InputLabel>State *</InputLabel>
                  <Select
                    value={shippingInfo.state}
                    label="State *"
                    onChange={(e) => handleInputChange('state', e.target.value)}
                  >
                    <MenuItem value="AL">Alabama</MenuItem>
                    <MenuItem value="AK">Alaska</MenuItem>
                    <MenuItem value="AZ">Arizona</MenuItem>
                    <MenuItem value="AR">Arkansas</MenuItem>
                    <MenuItem value="CA">California</MenuItem>
                    <MenuItem value="CO">Colorado</MenuItem>
                    <MenuItem value="CT">Connecticut</MenuItem>
                    <MenuItem value="DE">Delaware</MenuItem>
                    <MenuItem value="FL">Florida</MenuItem>
                    <MenuItem value="GA">Georgia</MenuItem>
                    <MenuItem value="HI">Hawaii</MenuItem>
                    <MenuItem value="ID">Idaho</MenuItem>
                    <MenuItem value="IL">Illinois</MenuItem>
                    <MenuItem value="IN">Indiana</MenuItem>
                    <MenuItem value="IA">Iowa</MenuItem>
                    <MenuItem value="KS">Kansas</MenuItem>
                    <MenuItem value="KY">Kentucky</MenuItem>
                    <MenuItem value="LA">Louisiana</MenuItem>
                    <MenuItem value="ME">Maine</MenuItem>
                    <MenuItem value="MD">Maryland</MenuItem>
                    <MenuItem value="MA">Massachusetts</MenuItem>
                    <MenuItem value="MI">Michigan</MenuItem>
                    <MenuItem value="MN">Minnesota</MenuItem>
                    <MenuItem value="MS">Mississippi</MenuItem>
                    <MenuItem value="MO">Missouri</MenuItem>
                    <MenuItem value="MT">Montana</MenuItem>
                    <MenuItem value="NE">Nebraska</MenuItem>
                    <MenuItem value="NV">Nevada</MenuItem>
                    <MenuItem value="NH">New Hampshire</MenuItem>
                    <MenuItem value="NJ">New Jersey</MenuItem>
                    <MenuItem value="NM">New Mexico</MenuItem>
                    <MenuItem value="NY">New York</MenuItem>
                    <MenuItem value="NC">North Carolina</MenuItem>
                    <MenuItem value="ND">North Dakota</MenuItem>
                    <MenuItem value="OH">Ohio</MenuItem>
                    <MenuItem value="OK">Oklahoma</MenuItem>
                    <MenuItem value="OR">Oregon</MenuItem>
                    <MenuItem value="PA">Pennsylvania</MenuItem>
                    <MenuItem value="RI">Rhode Island</MenuItem>
                    <MenuItem value="SC">South Carolina</MenuItem>
                    <MenuItem value="SD">South Dakota</MenuItem>
                    <MenuItem value="TN">Tennessee</MenuItem>
                    <MenuItem value="TX">Texas</MenuItem>
                    <MenuItem value="UT">Utah</MenuItem>
                    <MenuItem value="VT">Vermont</MenuItem>
                    <MenuItem value="VA">Virginia</MenuItem>
                    <MenuItem value="WA">Washington</MenuItem>
                    <MenuItem value="WV">West Virginia</MenuItem>
                    <MenuItem value="WI">Wisconsin</MenuItem>
                    <MenuItem value="WY">Wyoming</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="ZIP Code *"
                  value={shippingInfo.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  disabled={isProcessing}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Payment Section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Payment Information
            </Typography>
            
            {isInitializing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Initializing payment...
                </Typography>
              </Box>
            ) : isDemoMode ? (
              <DemoPaymentForm
                shippingInfo={shippingInfo}
                cartItems={cartItems}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                validateForm={validateForm}
              />
            ) : clientSecret ? (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CheckoutForm
                  shippingInfo={shippingInfo}
                  cartItems={cartItems}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  isProcessing={isProcessing}
                  setIsProcessing={setIsProcessing}
                  validateForm={validateForm}
                />
              </Elements>
            ) : (
              <Alert severity="error">
                Failed to initialize payment. Please try refreshing the page.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 'fit-content', position: 'sticky', top: 20 }}>
            <Typography variant="h5" gutterBottom>
              Order Summary
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              {cartItems.map((item) => (
                <Box key={item.card.id} sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.card.player} ({item.quantity}x)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.card.year} {item.card.brand} {item.card.team}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatPrice(item.card.price * item.quantity)}
                  </Typography>
                </Box>
              ))}
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1">{formatPrice(getCartTotal())}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Shipping:</Typography>
                <Typography variant="body1">Free</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold">Total:</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ 
                  color: mode === 'light' ? '#000000' : '#FFFFFF'
                }}>
                  {formatPrice(getCartTotal())}
                </Typography>
              </Box>
            </Box>
            
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              Secure payment powered by Stripe
            </Alert>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CheckoutPage;
